import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { ContentDetailModal } from '@/components/modals/ContentDetailModal';
import type { CalendarEvent } from '@/services/business/calendar';
import type { ConnectedPlatform } from '@/services/data/socialConnections';

interface DesktopContentDetailOverlayProps {
  selectedEvent: CalendarEvent | null;
  connectedPlatforms: ConnectedPlatform[];
  onClose: () => void;
  onEventUpdate: (eventId: string, patch: Partial<CalendarEvent>) => void;
  onValidate: () => Promise<void>;
}

export function DesktopContentDetailOverlay({
  selectedEvent,
  connectedPlatforms,
  onClose,
  onEventUpdate,
  onValidate,
}: DesktopContentDetailOverlayProps) {
  const isContentOrAb =
    selectedEvent?.type === 'content' || selectedEvent?.type === 'ab_testing';

  return (
    <AnimatePresence>
      {isContentOrAb && (
        <motion.div
          key="lightbox-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 z-[100] bg-[#0E0E0E]/95 backdrop-blur-sm flex flex-col overflow-hidden"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-5 right-5 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <ContentDetailModal
              event={selectedEvent as any}
              isOpen={!!selectedEvent}
              onClose={onClose}
              connectedPlatforms={connectedPlatforms}
              variant="inline"
              isAutoValidated={selectedEvent?.contentStatus === 'auto_validated'}
              onEventUpdate={onEventUpdate}
              onValidate={onValidate}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
